import { Col, Collapse, InputNumber, Row } from 'antd'
import { format, parse } from 'date-fns'
import { Form, Formik, type FormikProps } from 'formik'
import { type FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from './manageapp.module.scss'

import { Button } from 'common/components/Button/Button'
import { FormControl } from 'common/components/FormItems/FormControl/FormControl'
import { Modal, ModalAction } from 'common/components/Modal'
import { Show } from 'common/components/Show/Show'
import {
  BUTTON_CONSTANTS,
  BUTTON_MODIFIER,
  BUTTON_PROPORTION,
  BUTTON_SEVERITY,
} from 'common/constants/buttonConstants'
import { FORM_CONTROL_TYPE } from 'common/constants/formControlConstants'
import { INPUT_TYPES } from 'common/constants/inputConstants'
import { MODAL_LABELS } from 'common/constants/modalConstants'
import { TIME_FORMAT } from 'common/constants/timeFormat'
import type { IOption } from 'common/interfaces/IOption'

import {
  COUNT_AS_OVERLAPPING,
  INFO_CONSTANTS,
  PATIENT_STATUSES,
  REMOTE_APPOINTMENT_CATEGORY_CODES,
  ROOM_STATUS,
  SMS_STATUSES,
} from 'features/Home/constants/infoConstants'
import {
  EHR_INITIAL_PATIENT_DATA,
  NEW_PATIENT_FORM_KEYS,
} from 'features/Home/constants/infoNewPatientFormKeys'
import { NEW_PATIENT_LABELS } from 'features/Home/constants/infoNewPatientLabels'
import type { IPatientConsultationStaff, IPatientRoom } from 'features/Home/interfaces/IInfoPatient'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import {
  type EHRAppointmentFormType,
  EHR_APPOINTMENT_SCHEMA,
} from 'features/Home/schemas/infoNewPatientSchema'
import { InfoService } from 'features/Home/services/infoService'
import { PATIENTS_LABELS } from 'features/Patients/constants/patientsConstants'
import type { IPatient } from 'features/Patients/interfaces/IPatient'

import { Icon } from '@iconify/react'
import { useDetectMobile } from 'app/hooks/useDetectMobile'
import { useNotification } from 'app/providers'
import { ReactComponent as EditSquareIcon } from 'assets/icons/edit-square.svg'
import { DatePicker } from 'common/components/DatePicker/DatePicker'
import { Input } from 'common/components/Input/Input'
import { Label } from 'common/components/Label/Label'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { BEVERLY_HILLS_SECOND_PRODUCTION } from 'common/constants/siteSelectorConstants'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { useApiResponse } from 'common/hooks/useApiResponse'
import type {
  ApiResponse,
  ITriggerRequest,
  ITriggerResponse,
} from 'common/interfaces/IRequestResponse'
import dayjs, { type Dayjs } from 'dayjs'
import isToday from 'dayjs/plugin/isToday'
import 'features/Home/Book/Booking/Schedule/PatientSchedule/Calendar/time-picker.css'
import { Warning } from 'features/Home/Book/Booking/Schedule/PatientSchedule/Calendar/Warning/Warning'
import { formatRoomName } from 'features/Home/Book/Booking/Schedule/PatientSchedule/DocumentResourceHeader/DocumentResourceHeader'
import { JitsiTelehealthCallButton } from 'features/JitsiTelehealth'
import {
  PatientFormModal,
  type PatientFormType,
} from 'features/Patients/components/PatientFormModal/PatientFormModal'
import { useCreatePatientMutation } from 'features/Patients/state/api/patientApi'
import { EncounterSelectModal } from 'features/Profile/features/TransactionsMapping/EncounterSelectModal'
import { useOverwriteAppointmentEncounterMutation } from 'features/Profile/features/TransactionsMapping/transactionsMapping.api'
import { SCHEDULE_TAGS, scheduleApi } from 'features/Schedule/api/schedule.api'
import {
  type AppointmentCategories,
  type ScheduleAppointmentDto,
} from 'features/Schedule/api/schedule.types'
import { useScheduleActions } from 'features/Schedule/pages/components/ScheduleDayView/hooks/useScheduleActions'
import { useScheduleWeekActions } from 'features/Schedule/pages/components/ScheduleWeekView/hooks/useScheduleWeekActions'
import { EScheduleView } from 'features/Schedule/pages/Schedule'
import { Link, useParams } from 'react-router'
import { PathUtils } from 'routes/routes.utils'
import { DeleteConfirmationDialog } from './DeleteConfirmationDialog'
import { FindAvailableModal } from './FindAvailableModal/FindAvailableModal'
import { PatientSearchModal } from './PatientSearchModal/PatientSearchModal'
import { SmsOptions } from './SmsOptions/SmsOptions'
import { type ManageAppointmentProps } from './types'

dayjs.extend(isToday)

const formatAuditDateTime = (value?: string | null): string => {
  if (!value) return ''
  const date = dayjs(value)
  return date.isValid() ? date.format('MM/DD/YYYY hh:mm A') : value
}

const JessicaPatient = {
  id: 23395,
  first_name: 'Jessica',
  last_name: 'Wu',
  phone: null,
  language_type_code: 1,
  punctuality_type_code: 0,
  punctuality_minutes: 0,
}

export const ManageEhrAppointment: FC<ManageAppointmentProps> = memo(
  ({
    setOpen,
    open,
    isEditing,
    editedPatientData,
    title = INFO_CONSTANTS.ADD_WALK_IN_PATIENT,
    disabledEverything = false,
    customAppointment,
    date: selectedDate,
    languages,
    consultation_staffs,
    rooms: roomsData,
    schedules,
    disabledIntervals,
    dayInterval,
    timeZone,
    appointmentCategories,
    byPassTemperatureCheck,
    patientStatuses,
    scheduleView,
    weekDays,
    punctuality = [],
    patientBufferTime = {
      after: 0,
      before: 0,
    },
    initialCategoryName,
    initialStatusCode,
    onDateChange,
    scheduleMonthData,
  }) => {
    const { siteId, day } = useParams()
    const isMobile = useDetectMobile()
    const isBeverlyHillsSecondProduction = siteId === BEVERLY_HILLS_SECOND_PRODUCTION

    const [isAddPatientOpen, setIsAddPatientOpen] = useState(false)

    const [isNotifyOpen, setNotifyOpen] = useState<boolean>(false)
    const [isLoadingDelete, setIsLoadingDelete] = useState(false)
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState<boolean>(false)
    const [selectedRoom, setSelectedRoom] = useState<IPatientRoom>(null)
    const [overlappingEvents, setOverlappingEvents] = useState<number>(0)
    const [overlappingDisabledInterval, setOverlappingDisabledInterval] = useState<boolean>(false)
    const [patientAccessCode, setPatientAccessCode] = useState<number | string | null>(null)
    const [awaitConfirmationSchedule, setAwaitConfirmationSchedule] = useState<any>(0)
    const [disabledUntil, setDisabledUntil] = useState(
      parseInt(localStorage.getItem(`patient-${editedPatientData?.id}-disabledUntil`) || '0', 10),
    )
    const [isPatientSearchOpen, setIsPatientSearchOpen] = useState<boolean>(false)
    const [isFindAvailableOpen, setIsFindAvailableOpen] = useState<boolean>(false)
    const [isEncounterModalOpen, setIsEncounterModalOpen] = useState(false)
    const [encounterError, setEncounterError] = useState<string | undefined>()
    const [mappedEncounterId, setMappedEncounterId] = useState<number | null>(null)
    const [mappedEncounterUuid, setMappedEncounterUuid] = useState<string | null>(null)

    const formRef = useRef<FormikProps<EHRAppointmentFormType>>(null)
    const resetCountryFunction = useRef<() => void | null>(null)

    const activeTelehealthAppointmentId = useAppSelector(
      (state) => state.jitsiTelehealthReducer.activeSession?.appointmentId ?? null,
    )
    const previousTelehealthAppointmentIdRef = useRef<number | null>(null)

    // When the telehealth call for the appointment open in this dialog ends
    // (active session goes from this appointment back to null), reflect the
    // checked-out status in the form's status dropdown.
    useEffect(() => {
      const previousId = previousTelehealthAppointmentIdRef.current
      previousTelehealthAppointmentIdRef.current = activeTelehealthAppointmentId

      if (
        previousId !== null &&
        activeTelehealthAppointmentId === null &&
        previousId === editedPatientData?.id
      ) {
        formRef.current?.setFieldValue(
          NEW_PATIENT_FORM_KEYS.PATIENT_STATUS,
          PATIENT_STATUSES.CHECKED_OUT,
        )
      }
    }, [activeTelehealthAppointmentId, editedPatientData?.id])

    const rooms = useMemo(() => {
      return [...roomsData].filter((room) => !!room.add_patient)
    }, [roomsData])

    const eventDate = useMemo(() => {
      return editedPatientData?.date
    }, [editedPatientData?.date])

    const { setNotification } = useNotification()
    const dispatch = useAppDispatch()
    const [overwriteAppointmentEncounter, { isLoading: isOverwritingEncounter }] =
      useOverwriteAppointmentEncounterMutation()

    const isPatientCheckedOut = useMemo(() => {
      return editedPatientData?.status?.code === PATIENT_STATUSES.CHECKED_OUT
    }, [editedPatientData?.status?.code])

    const scheduleActions =
      scheduleView === EScheduleView.WEEK ? useScheduleWeekActions() : useScheduleActions()
    const { createAppointment, updateAppointment, notifyPatient, deleteAppointment, isLoading } =
      scheduleActions

    const { processApiResponse } = useApiResponse()
    const [createPatient]: ITriggerRequest<ITriggerResponse> = useCreatePatientMutation()

    const isDateDisabled = useCallback(
      (date: Dayjs) => {
        const dateKey = date.format('YYYY-MM-DD')
        const dayData = scheduleMonthData?.[dateKey]

        if (dayData && dayData.active === 0) {
          return true
        }

        return false
      },
      [scheduleMonthData],
    )

    useEffect(() => {
      if (isEditing) {
        setSelectedRoom(editedPatientData?.room)
      }
      if (title === INFO_CONSTANTS.ADD_WALK_IN_PATIENT) {
        resetCountryFunction.current?.()
      }
      if (customAppointment) {
        const [label, value] = customAppointment.room.split('/')
        const room = rooms.find((room: any) => room.label === label && room.value === value)
        setSelectedRoom(room)
      }
    }, [isEditing, customAppointment, title])

    useEffect(() => {
      const selectedSchedule = schedules.find(
        (schedule: IPatientSchedule) => schedule.id === editedPatientData?.id,
      )

      if (selectedSchedule && selectedSchedule?.access_code) {
        setPatientAccessCode(selectedSchedule?.access_code)
      }
    }, [schedules])

    useEffect(() => {
      if (editedPatientData) {
        setPatientAccessCode(editedPatientData.access_code)

        const storedDisabledUntil = parseInt(
          localStorage.getItem(`patient-${editedPatientData.id}-disabledUntil`) || '0',
          10,
        )
        setDisabledUntil(storedDisabledUntil)
      }
    }, [editedPatientData])

    useEffect(() => {
      setMappedEncounterId(null)
      setMappedEncounterUuid(null)
      setEncounterError(undefined)
      setIsEncounterModalOpen(false)
    }, [editedPatientData?.id])

    useEffect(() => {
      if (disabledUntil > Date.now()) {
        const remainingTime = disabledUntil - Date.now()
        const timer = setTimeout(() => {
          setDisabledUntil(0)
        }, remainingTime)

        return () => clearTimeout(timer)
      }
    }, [disabledUntil])

    useEffect(() => {
      if (!onDateChange || !open || isEditing || !formRef.current) return

      const currentRoomValue = formRef.current.values?.room
      const matchedRoom = rooms.find((room) => String(room.value) === String(currentRoomValue))

      if (matchedRoom) {
        setSelectedRoom(matchedRoom)
        return
      }

      if (rooms.length === 1) {
        const [onlyRoom] = rooms
        setSelectedRoom(onlyRoom)
        formRef.current.setFieldValue(NEW_PATIENT_FORM_KEYS.ROOM, onlyRoom.value)
        return
      }

      setSelectedRoom(null)

      if (currentRoomValue) {
        formRef.current.setFieldValue(NEW_PATIENT_FORM_KEYS.ROOM, '')
      }
    }, [open, isEditing, onDateChange, rooms])

    const displayDeletePatientTrash = useMemo(
      () => isEditing,
      [isEditing, disabledEverything, editedPatientData],
    )

    const constructAppointmentArray = (time: Date, duration: number) => {
      if (!time || !duration) return null
      const startTime = dayjs(time)
      const endTime = startTime.add(duration, 'minutes')
      return [startTime.toDate(), endTime.toDate()]
    }

    const updateOverlappingSlots = (room: IPatientRoom, values: any) => {
      if (!room) return
      const [start, end] = values

      const isInsideDisabledInterval = disabledIntervals.some((disabledInterval: any) =>
        InfoService.checkIsOverlappingIntervals(
          disabledInterval,
          start,
          end,
          `${room.label}/${room.value}`,
        ),
      )
      setOverlappingDisabledInterval(isInsideDisabledInterval)
      if (isInsideDisabledInterval) {
        return
      }

      const eventsFromRoom = schedules
        .filter(
          (schedule: IPatientSchedule): boolean =>
            schedule.room?.value === room.value &&
            schedule.id !== editedPatientData?.id &&
            COUNT_AS_OVERLAPPING.includes(schedule.status.code),
        )
        .map((item: IPatientSchedule) => ({
          id: item.id,
          title: item.patient.name,
          start: new Date(`${item.date} ${item.start_time}`),
          end: new Date(`${item.date} ${item.end_time}`),
          resourceId: item?.room?.label,
          status: item.status,
          temperature_bypass: item.temperature_bypass,
          isDraggable: true,
          current_location: item.current_location,
          appointment_start_time: item.appointment_start_time,
          appointment_end_time: item.appointment_end_time,
          original_room: item.original_room,
          room_type_id: item.room.room_type_id,
        }))

      const { nrOfOverlappingEvents } = InfoService.getNumberOfOverlappingIntervals(
        { start, end, resourceId: room.label },
        eventsFromRoom,
        room.label,
      )
      setOverlappingEvents(nrOfOverlappingEvents)
      return nrOfOverlappingEvents
    }

    const onFieldChange = ({
      field,
      fieldValue,
      setFieldValue,
      values,
      setErrors,
      errors,
    }: {
      field: string
      fieldValue: any
      setFieldValue: (field: string, value: any) => void
      values?: any
      option?: any
      setErrors?: any
      errors?: any
    }): void => {
      const appointmentArray = constructAppointmentArray(values.time, values.duration)

      if (field === 'first_name' || field === 'last_name') {
        updateOverlappingSlots(selectedRoom, appointmentArray)
        return
      }

      const validFields = [
        NEW_PATIENT_FORM_KEYS.LANGUAGE,
        NEW_PATIENT_FORM_KEYS.CATEGORY_TYPE_CODE,
        NEW_PATIENT_FORM_KEYS.CONSULTATION_STAFF,
      ]

      if (validFields.includes(field as NEW_PATIENT_FORM_KEYS)) {
        setFieldValue(field, fieldValue)
      }

      if (field === NEW_PATIENT_FORM_KEYS.CATEGORY_TYPE_CODE) {
        const category = appointmentCategories.find(
          (category: AppointmentCategories) => category.code === fieldValue,
        )
        setFieldValue(NEW_PATIENT_FORM_KEYS.DURATION, category?.duration ?? 5)
        setFieldValue(NEW_PATIENT_FORM_KEYS.TITLE, category?.name)

        if (isBeverlyHillsSecondProduction) {
          // for Doctor N/A category, set automatically NONE status
          const doctorNACategory = 'Doctor N/A'
          const categoryName = category?.name?.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
          const isDoctorNACategory = categoryName?.includes(
            doctorNACategory.replace(/[^a-zA-Z0-9]/g, '').toLowerCase(),
          )

          if (isDoctorNACategory) {
            setFieldValue(NEW_PATIENT_FORM_KEYS.PATIENT_STATUS, PATIENT_STATUSES.NONE)

            setFieldValue(NEW_PATIENT_FORM_KEYS.ID, JessicaPatient.id)
            setFieldValue(NEW_PATIENT_FORM_KEYS.FIRST_NAME, JessicaPatient.first_name)
            setFieldValue(NEW_PATIENT_FORM_KEYS.LAST_NAME, JessicaPatient.last_name)
            setFieldValue(NEW_PATIENT_FORM_KEYS.PHONE, JessicaPatient.phone)
            setFieldValue(NEW_PATIENT_FORM_KEYS.LANGUAGE, JessicaPatient.language_type_code)
            setFieldValue(
              NEW_PATIENT_FORM_KEYS.PUNCTUALITY_TYPE_CODE,
              JessicaPatient.punctuality_type_code,
            )
            setFieldValue(
              NEW_PATIENT_FORM_KEYS.PUNCTUALITY_MINUTES,
              JessicaPatient.punctuality_minutes,
            )
          }
        }
      }

      if (field === 'room') {
        const room: IPatientRoom | undefined = rooms.find(
          (room: IPatientRoom): boolean => room.value === fieldValue,
        )
        setSelectedRoom(room)
        updateOverlappingSlots(room, appointmentArray)

        const updatedErrors = { ...errors }
        delete updatedErrors[NEW_PATIENT_FORM_KEYS.APPOINTMENT]
        setErrors({ ...updatedErrors })
      }

      if (field === 'appointment') {
        updateOverlappingSlots(selectedRoom, fieldValue)
        const [_, selectedEndTime] = fieldValue

        // Get slots based on view type (day vs week)
        const getSlotsForDay = () => {
          if (scheduleView === EScheduleView.WEEK && weekDays) {
            const formattedDate = dayjs(eventDate).format('YYYY-MM-DD')
            const daySlots = weekDays[formattedDate]?.time_slots || []
            return daySlots.map((slot) => ({
              ...slot,
              start_time: slot.start_time,
              end_time: slot.end_time,
            }))
          }
          return selectedRoom?.slots || []
        }

        const daySlots = getSlotsForDay()
        const sortedSlots = InfoService.getSortedSlots(daySlots, editedPatientData?.date)
        const endOfTheSchedule = sortedSlots.at(-1)?.end_time

        const isAfterSchedule =
          endOfTheSchedule && dayjs(selectedEndTime).isAfter(endOfTheSchedule, 'minutes')

        if (isAfterSchedule) {
          formRef.current?.setFieldError(
            NEW_PATIENT_FORM_KEYS.APPOINTMENT,
            INFO_CONSTANTS.TIME_OUTSIDE_SCHEDULE,
          )
        }
      }
    }

    const clearModalData = () => {
      setOpen()
      setSelectedRoom(null)
      setOverlappingEvents(0)
      setOverlappingDisabledInterval(false)
      setIsEncounterModalOpen(false)
      setEncounterError(undefined)
      setMappedEncounterId(null)
      setMappedEncounterUuid(null)
      document.querySelectorAll('.e-selected-cell')?.forEach((el) => {
        el.classList.remove('e-selected-cell')
      })
    }

    const handleEncounterConfirm = useCallback(
      async ({ encounterId, encounterUuid }: { encounterId: number; encounterUuid: string }) => {
        if (!editedPatientData?.id || !siteId) return

        try {
          const response = await overwriteAppointmentEncounter({
            appointment_id: editedPatientData.id,
            encounter_id: encounterId,
            encounter_uuid: encounterUuid,
            siteId,
          }).unwrap()

          if (response && response.status === false) {
            const message = response.message || 'Failed to map encounter'
            setEncounterError(message)
            setNotification({
              title: 'Error',
              description: message,
              type: ALERT_CONSTANTS.ERROR,
            })
            return
          }

          setMappedEncounterId(encounterId)
          setMappedEncounterUuid(encounterUuid)
          setEncounterError(undefined)
          setIsEncounterModalOpen(false)
          dispatch(
            scheduleApi.util.invalidateTags([SCHEDULE_TAGS.SCHEDULE, SCHEDULE_TAGS.SCHEDULE_WEEK]),
          )
          setNotification({
            title: 'Success',
            description: response?.message || 'Encounter mapped successfully',
            type: ALERT_CONSTANTS.SUCCESS,
          })
        } catch (error: any) {
          const message = error?.data?.[0] || error?.message || 'Failed to map encounter'
          setEncounterError(message)
          setNotification({
            title: 'Error',
            description: message,
            type: ALERT_CONSTANTS.ERROR,
          })
        }
      },
      [dispatch, editedPatientData?.id, overwriteAppointmentEncounter, setNotification, siteId],
    )
    const handleCreateAppointment = async (data: EHRAppointmentFormType) => {
      if (isLoading) return

      const {
        id,
        room,
        time,
        duration,
        temperature_bypass,
        first_name,
        last_name,
        phone,
        language,
        language_type_code,
        consultation_staff,
        category_type_code,
        title,
        comments,
        patient_status,
        punctuality_type_code,
        punctuality_minutes,
      } = data

      const patient = {
        id,
        first_name,
        last_name,
        phone,
        language_type_code: language || language_type_code,
      }
      const adjustedData = {
        room,
        patient,
        consultation_staff,
        temperature_bypass: temperature_bypass ? 1 : 0,
        start_time: format(time, TIME_FORMAT.TIME_PICKER_HH_SS),
        end_time: format(
          dayjs(time).add(duration, 'minutes').toDate(),
          TIME_FORMAT.TIME_PICKER_HH_SS,
        ),
        category_type_code,
        title,
        comments,
        status_type_code: patient_status,
        punctuality_type_code: Number(punctuality_type_code),
        punctuality_minutes,
      } as ScheduleAppointmentDto

      const closeAndReset = () => {
        clearModalData()
        formRef.current?.resetForm()
      }

      const handleCreatePatient = async () => {
        await createAppointment(adjustedData, dayjs(data.date).format('YYYY-MM-DD'))
        closeAndReset()
      }

      handleCreatePatient()
    }

    const timePickerOptions = useMemo(() => {
      if (!selectedRoom) {
        return []
      }

      // Get slots based on view type (day vs week)
      const getSlotsForDay = () => {
        // Week view: get slots from the specific day
        if (scheduleView === EScheduleView.WEEK && weekDays) {
          const daySlots = weekDays[eventDate]?.time_slots || []
          return daySlots.map((slot) => ({
            ...slot,
            start_time: slot.start_time,
            end_time: slot.end_time,
          }))
        }

        // Day view: use room slots
        return selectedRoom?.slots || []
      }

      const daySlots = getSlotsForDay()
      const sortedSlots = InfoService.getSortedSlots(daySlots, eventDate)

      if (sortedSlots.length === 0) {
        return []
      }

      const start_time = sortedSlots[0].start_time
      const end_time = sortedSlots.at(-1).end_time

      return [{ start_time, end_time }]
    }, [schedules, selectedRoom, eventDate, editedPatientData, scheduleView, weekDays])

    const proceedToUpdateAppointment = async (scheduleData: any) => {
      await updateAppointment(scheduleData, scheduleData.date, editedPatientData?.id)
      clearModalData()
      formRef.current?.resetForm()
    }

    const handleNotify = async () => {
      await notifyPatient(editedPatientData?.id)
      setNotifyOpen(false)
      const newDisabledUntil = Date.now() + 60 * 1000
      localStorage.setItem(
        `patient-${editedPatientData?.id}-disabledUntil`,
        newDisabledUntil.toString(),
      )
      setDisabledUntil(newDisabledUntil)
    }

    // Patient-facing telehealth link. Some patients have no email/phone (or have
    // not consented to SMS), so staff need to copy this link and send it manually
    // through their own email/SMS client.
    const telehealthPatientLink = editedPatientData?.jitsi_telehealth?.patient_short_link ?? ''

    const handleCopyTelehealthLink = async () => {
      if (!telehealthPatientLink) return
      try {
        await navigator.clipboard.writeText(telehealthPatientLink)
        setNotification({
          title: 'Link copied',
          description: 'Telehealth link copied. Paste it into your email or SMS to the patient.',
          type: ALERT_CONSTANTS.SUCCESS,
        })
      } catch {
        setNotification({
          title: 'Copy failed',
          description: 'Could not copy the link. Please copy it manually.',
          type: ALERT_CONSTANTS.ERROR,
        })
      }
    }

    const handleUpdateAppointment = async (data: any) => {
      if (isLoading) return

      const {
        room,
        time,
        duration,
        consultation_staff,
        temperature_bypass,
        first_name,
        last_name,
        phone,
        language_type_code,
        category_type_code,
        title,
        comments,
        patient_status,
        date,
        punctuality_type_code,
        punctuality_minutes,
      } = data

      const patient = {
        id: editedPatientData?.patient?.id,
        first_name,
        last_name,
        phone,
        language_type_code,
      }

      const adjustedData = {
        date: dayjs(date).format('YYYY-MM-DD'),
        room,
        patient,
        consultation_staff,
        temperature_bypass: temperature_bypass ? 1 : 0,
        start_time: format(time, TIME_FORMAT.TIME_PICKER_HH_SS),
        end_time: format(
          dayjs(time).add(duration, 'minutes').toDate(),
          TIME_FORMAT.TIME_PICKER_HH_SS,
        ),
        category_type_code,
        title,
        comments,
        status_type_code: patient_status,
        punctuality_type_code: Number(punctuality_type_code),
        punctuality_minutes,
      }

      if (overlappingEvents > 1) {
        const awaitConfirmationData = {
          scheduleData: adjustedData,
          resetFormCallback: formRef.current?.resetForm,
          confirmAction: proceedToUpdateAppointment,
        }
        setAwaitConfirmationSchedule(awaitConfirmationData)
        return
      }

      proceedToUpdateAppointment(adjustedData)
    }

    const handleConfirmUpdateAppointment = async () => {
      const { scheduleData, resetFormCallback, confirmAction } = awaitConfirmationSchedule
      confirmAction(scheduleData, resetFormCallback)
      setAwaitConfirmationSchedule(null)
    }
    const defaultLanguageName = languages.find((lang: IOption) => lang.code === 1)?.name

    const defaultConsultationStaff = consultation_staffs.find(
      (consultation_staff: IPatientConsultationStaff) => consultation_staff.is_doctor === 1,
    )

    const getInitialFormValues = () => {
      if (isEditing) {
        return {
          [NEW_PATIENT_FORM_KEYS.ID]: editedPatientData?.id,
          [NEW_PATIENT_FORM_KEYS.FIRST_NAME]: editedPatientData?.patient.first_name,
          [NEW_PATIENT_FORM_KEYS.LAST_NAME]: editedPatientData?.patient.last_name,
          [NEW_PATIENT_FORM_KEYS.PHONE]: editedPatientData?.patient.phone,

          [NEW_PATIENT_FORM_KEYS.LANGUAGE]: editedPatientData?.patient.language_type_code,
          [NEW_PATIENT_FORM_KEYS.ROOM]:
            typeof editedPatientData?.room === 'string'
              ? editedPatientData?.room
              : editedPatientData?.room?.value,
          [NEW_PATIENT_FORM_KEYS.TIME]: editedPatientData?.start_time,
          [NEW_PATIENT_FORM_KEYS.CATEGORY_TYPE_CODE]: editedPatientData?.category?.code,
          [NEW_PATIENT_FORM_KEYS.CONSULTATION_STAFF]: editedPatientData?.consultation_staff,
          [NEW_PATIENT_FORM_KEYS.TEMPERATURE_BYPASS]: Boolean(
            editedPatientData?.temperature_bypass,
          ),
          [NEW_PATIENT_FORM_KEYS.TITLE]: editedPatientData?.title,
          [NEW_PATIENT_FORM_KEYS.DURATION]: dayjs(editedPatientData?.end_time).diff(
            dayjs(editedPatientData?.start_time),
            'minutes',
          ),
          [NEW_PATIENT_FORM_KEYS.DATE]: editedPatientData?.date,
          [NEW_PATIENT_FORM_KEYS.COMMENTS]: editedPatientData?.comments || '',
          [NEW_PATIENT_FORM_KEYS.PATIENT_STATUS]: editedPatientData?.status?.code ?? '',
          [NEW_PATIENT_FORM_KEYS.PUNCTUALITY_TYPE_CODE]:
            editedPatientData?.punctuality?.appointment?.code,
          [NEW_PATIENT_FORM_KEYS.PUNCTUALITY_MINUTES]:
            editedPatientData?.punctuality?.appointment?.minutes,
          [NEW_PATIENT_FORM_KEYS.PATIENT_PUNCTUALITY]: {
            code: editedPatientData?.punctuality?.patient?.code,
            minutes: editedPatientData?.punctuality?.patient?.minutes,
          },
        }
      } else if (!isEditing && !customAppointment) {
        const findDefaultCategory = initialCategoryName
          ? appointmentCategories.find((category: AppointmentCategories) =>
              category.name.toLowerCase().includes(initialCategoryName.toLowerCase()),
            ) ??
            appointmentCategories.find(
              (category: AppointmentCategories) => category.default === 1,
            ) ??
            appointmentCategories[0]
          : appointmentCategories.find(
              (category: AppointmentCategories) => category.default === 1,
            ) ?? appointmentCategories[0]

        const defaultPatientStatus =
          initialStatusCode !== undefined
            ? patientStatuses?.find((status) => status.code === initialStatusCode) ??
              patientStatuses?.find((status) => status.default === 1) ??
              patientStatuses?.[0]
            : patientStatuses?.find((status) => status.default === 1) ?? patientStatuses?.[0]

        return {
          ...EHR_INITIAL_PATIENT_DATA,
          [NEW_PATIENT_FORM_KEYS.LANGUAGE]: defaultLanguageName,
          [NEW_PATIENT_FORM_KEYS.CONSULTATION_STAFF]: defaultConsultationStaff?.id,
          [NEW_PATIENT_FORM_KEYS.CATEGORY_TYPE_CODE]: findDefaultCategory?.code || '',
          [NEW_PATIENT_FORM_KEYS.TITLE]: findDefaultCategory?.name || '',
          [NEW_PATIENT_FORM_KEYS.TEMPERATURE_BYPASS]: byPassTemperatureCheck,
          [NEW_PATIENT_FORM_KEYS.TIME]: new Date(),
          [NEW_PATIENT_FORM_KEYS.DURATION]: findDefaultCategory?.duration ?? 5,
          [NEW_PATIENT_FORM_KEYS.DATE]: eventDate || selectedDate,
          [NEW_PATIENT_FORM_KEYS.PATIENT_STATUS]: defaultPatientStatus?.code ?? '',
        }
      }

      if (customAppointment) {
        const [name, code] = customAppointment.room.split('/')

        const room: any =
          scheduleView === EScheduleView.DAY
            ? rooms.find((room: any) => room.name === name && room.code === code)
            : rooms.find((room: any) => room.is_waiting_room)

        const findDefaultCategory =
          appointmentCategories.find((category: AppointmentCategories) => category.default === 1) ??
          appointmentCategories[0]

        const defaultPatientStatus =
          patientStatuses?.find((status) => status.default === 1) ?? patientStatuses?.[0]

        return {
          ...EHR_INITIAL_PATIENT_DATA,
          [NEW_PATIENT_FORM_KEYS.APPOINTMENT]: customAppointment?.appointment,
          [NEW_PATIENT_FORM_KEYS.ROOM]: room?.code,
          [NEW_PATIENT_FORM_KEYS.LANGUAGE]: defaultLanguageName,
          [NEW_PATIENT_FORM_KEYS.CONSULTATION_STAFF]: defaultConsultationStaff?.id,
          [NEW_PATIENT_FORM_KEYS.CATEGORY_TYPE_CODE]: findDefaultCategory?.code || '',
          [NEW_PATIENT_FORM_KEYS.TITLE]: findDefaultCategory?.name || '',
          [NEW_PATIENT_FORM_KEYS.TEMPERATURE_BYPASS]: byPassTemperatureCheck,
          [NEW_PATIENT_FORM_KEYS.DURATION]: findDefaultCategory?.duration ?? 5,
          [NEW_PATIENT_FORM_KEYS.DATE]: customAppointment?.appointment?.[0],
          [NEW_PATIENT_FORM_KEYS.PATIENT_STATUS]: defaultPatientStatus?.code ?? '',
          [NEW_PATIENT_FORM_KEYS.TIME]: new Date(customAppointment?.appointment?.[0]),
        }
      }

      return EHR_INITIAL_PATIENT_DATA
    }

    useEffect(() => {
      if (open) {
        formRef.current?.setValues(getInitialFormValues() as unknown as EHRAppointmentFormType)
      }
    }, [open])

    const handleRemoveSchedule = useCallback(
      async (sendSms: boolean) => {
        setIsLoadingDelete(true)
        const sendSmsValue = sendSms ? 1 : 0
        await deleteAppointment(eventDate, editedPatientData?.id, sendSmsValue)
          .then((response: any) => {
            setIsLoadingDelete(false)
            setNotification({
              title: 'Delete Success',
              description: response.message,
              type: ALERT_CONSTANTS.SUCCESS,
            })
          })
          .catch(() => {
            setIsLoadingDelete(false)
          })
        setIsDeleteConfirmOpen(false)
        clearModalData()
        setOpen()
      },
      [editedPatientData, eventDate, deleteAppointment],
    )

    const handleShowDeleteConfirmation = () => {
      setIsDeleteConfirmOpen(true)
    }

    const handleDeleteConfirm = async (sendSms: boolean) => {
      await handleRemoveSchedule(sendSms)
    }

    const handleDeleteCancel = () => {
      setIsDeleteConfirmOpen(false)
    }

    const getRoomErrorMessage = () => {
      if (!selectedRoom) return null

      const shouldApplyRoomRule = !InfoService.checkIfCurrentDateOverlapDisabledIntervals(
        disabledIntervals,
        selectedRoom,
        dayInterval,
        timeZone,
      )
      const excludedRooms = [ROOM_STATUS.FREE, ROOM_STATUS.CLEANUP, ROOM_STATUS.BUSY]

      const shouldDisplayRoomWarning =
        selectedRoom &&
        selectedRoom?.status &&
        !excludedRooms.includes(selectedRoom?.status?.code) &&
        shouldApplyRoomRule

      if (shouldDisplayRoomWarning) {
        return INFO_CONSTANTS.BUSY_ROOM_WARNING
      }
      if (overlappingEvents > selectedRoom?.max_patients && !selectedRoom?.is_waiting_room) {
        return `You reached the max number of ${
          selectedRoom?.max_patients || 1
        } patients at the same time for this room.`
      }
    }

    function preSubmit() {
      const { setFieldError } = formRef.current
      if (overlappingDisabledInterval) {
        setFieldError('appointment', INFO_CONSTANTS.DISABLED_INTERVAL_WARNING)
        return
      }
      onSubmit()
    }

    function onSubmit() {
      const { values } = formRef.current
      const data = { ...values }

      if (isEditing) {
        handleUpdateAppointment(data)
      } else {
        handleCreateAppointment(data)
      }
    }

    const handlePatientSelect = (patient: IPatient) => {
      if (patient) {
        formRef.current?.setValues(
          {
            ...formRef.current?.values,
            [NEW_PATIENT_FORM_KEYS.ID]: patient.id,
            [NEW_PATIENT_FORM_KEYS.FIRST_NAME]: patient.first_name,
            [NEW_PATIENT_FORM_KEYS.LAST_NAME]: patient.last_name,
            [NEW_PATIENT_FORM_KEYS.PHONE]: patient.phone,
            [NEW_PATIENT_FORM_KEYS.LANGUAGE]: patient.language_type_code,
            [NEW_PATIENT_FORM_KEYS.PUNCTUALITY_TYPE_CODE]: 0,
            [NEW_PATIENT_FORM_KEYS.PUNCTUALITY_MINUTES]: 0,
            [NEW_PATIENT_FORM_KEYS.PATIENT_PUNCTUALITY]: {
              code: patient.punctuality_type_code,
              minutes: patient.punctuality_minutes,
            },
          } as EHRAppointmentFormType,
          true,
        )
      }
      setIsPatientSearchOpen(false)
    }

    const handleSubmitPatientForm = useCallback(
      async (patient: PatientFormType) => {
        const { first_name, last_name, phone, language_type_code, doctor_emr_id } = patient
        const response: ApiResponse = await createPatient({
          first_name,
          last_name,
          phone,
          language_type_code,
          doctor_emr_id,
        })

        processApiResponse(response, {
          success: 'Create patient success',
          error: 'Create patient error',
        })

        if (response?.data?.patient) {
          handlePatientSelect(response.data.patient)
        }
      },
      [createPatient],
    )

    const handleCloseFormModal = useCallback(() => {
      setIsAddPatientOpen(false)
    }, [])

    const handleFindAvailableTimeSelect = (params: { startTime: Dayjs; endTime: Dayjs }) => {
      if (!onDateChange) {
        const waitingRoom = rooms.find((room: any) => room.is_waiting_room)
        formRef.current?.setValues({
          ...formRef.current?.values,
          [NEW_PATIENT_FORM_KEYS.TIME]: params.startTime.toDate(),
          [NEW_PATIENT_FORM_KEYS.DATE]: dayjs(params.startTime).format('YYYY-MM-DD'),
          ...(!dayjs(params.startTime).isToday() && {
            [NEW_PATIENT_FORM_KEYS.ROOM]: waitingRoom?.value,
          }),
        } as EHRAppointmentFormType)

        return
      }

      const nextDate = dayjs(params.startTime).format('YYYY-MM-DD')

      formRef.current?.setValues({
        ...formRef.current?.values,
        [NEW_PATIENT_FORM_KEYS.TIME]: params.startTime.toDate(),
        [NEW_PATIENT_FORM_KEYS.DATE]: nextDate,
      } as EHRAppointmentFormType)

      onDateChange?.(nextDate)
    }

    const calculateMaxDuration = useCallback(
      (selectedTime: Date) => {
        if (!dayInterval || !selectedTime) return 999

        const selectedTimeStr = dayjs(selectedTime).format('HH:mm')
        const dayIntervalMax = dayInterval.max

        const selectedMinutes = dayjs(`2000-01-01 ${selectedTimeStr}`).diff(
          dayjs('2000-01-01 00:00'),
          'minute',
        )
        const maxMinutes = dayjs(`2000-01-01 ${dayIntervalMax}`).diff(
          dayjs('2000-01-01 00:00'),
          'minute',
        )

        const availableMinutes = maxMinutes - selectedMinutes

        return Math.min(availableMinutes, 999)
      },
      [dayInterval],
    )

    return (
      <>
        <PatientFormModal
          isOpen={isAddPatientOpen}
          onClose={handleCloseFormModal}
          onSubmit={handleSubmitPatientForm}
          languages={languages}
        />
        <div className={styles.parent}>
          <Formik<EHRAppointmentFormType>
            innerRef={formRef}
            initialValues={{} as unknown as EHRAppointmentFormType}
            onSubmit={preSubmit}
            enableReinitialize
            validationSchema={EHR_APPOINTMENT_SCHEMA}
            validateOnBlur
            validateOnChange={false}>
            {({ setFieldValue, resetForm, values, errors, touched, setErrors }) => {
              const roomErrorMessage = getRoomErrorMessage()
              const phones = {
                cell_phone: values.phone,
              }

              const hasPhone = Object.values(phones).some((phone) => phone)

              const fullName = values?.first_name
                ? `${values?.last_name}, ${values?.first_name}`
                : ''

              // Get current max duration based on selected time
              const currentMaxDuration = useMemo(() => {
                return calculateMaxDuration(values?.time)
              }, [calculateMaxDuration, values?.time])

              const patientChartId = editedPatientData?.patient?.id
              const currentEncounterId =
                mappedEncounterId ?? editedPatientData?.emr_encounter_id ?? null
              const currentEncounterUuid =
                mappedEncounterUuid ?? editedPatientData?.emr_encounter_uuid ?? null
              const modalTitle = isEditing ? (
                <div className={styles.parentModalHeader}>
                  <div className={styles.parentTitleRow}>
                    <span className={styles.parentTitleText}>{title}</span>
                    {patientChartId ? (
                      <Link
                        to={PathUtils.getCharts(siteId, patientChartId, currentEncounterUuid)}
                        className={styles.parentChartsButton}
                        aria-label={INFO_CONSTANTS.OPEN_PATIENT_CHART}
                        title={INFO_CONSTANTS.OPEN_PATIENT_CHART}>
                        {INFO_CONSTANTS.VIEW_IN_CHART}
                        <Icon icon='gridicons:external' fontSize={18} />
                      </Link>
                    ) : null}
                  </div>
                  <p className={styles.encounterDescription}>
                    Encounter:{' '}
                    <button
                      type='button'
                      className={styles.encounterDescriptionLink}
                      onClick={() => setIsEncounterModalOpen(true)}>
                      <span>{currentEncounterId ?? 'Not Found'}</span>
                      <EditSquareIcon className={styles.encounterDescriptionIcon} />
                    </button>
                  </p>
                </div>
              ) : (
                title
              )

              return (
                <Modal
                  footer={null}
                  classNames={{ content: styles.modalContainer }}
                  title={modalTitle}
                  destroyOnHidden
                  open={open && !awaitConfirmationSchedule}
                  keyboard
                  cancelButton
                  onCancel={() => {
                    clearModalData()
                    resetForm()
                  }}>
                  <Form className={styles.parentForm}>
                    <Row gutter={16} align='bottom'>
                      <Col xs={isMobile && hasPhone ? 24 : isMobile ? 16 : 12}>
                        <Input
                          disabled={isEditing || isPatientCheckedOut}
                          value={fullName}
                          required
                          label='Patient'
                          name='patient_selector'
                          placeholder='Click to select'
                          readOnly
                          onClick={() => setIsPatientSearchOpen(true)}
                          className={styles.patientSelector}
                        />
                      </Col>
                      {!isMobile && (
                        <Show when={hasPhone}>
                          <Col xs={12}>
                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                              <div style={{ flex: 1 }}>
                                <Input
                                  disabled
                                  value={values.phone}
                                  label='Cell Phone'
                                  name='phone_display'
                                  readOnly
                                />
                              </div>
                              {!isEditing && (
                                <Button
                                  onClick={() => setIsAddPatientOpen(true)}
                                  disabled={isPatientCheckedOut}
                                  className={styles.parentCompactButton}>
                                  New patient
                                </Button>
                              )}
                            </div>
                          </Col>
                        </Show>
                      )}
                      {!isMobile && !isEditing && !hasPhone && (
                        <Col>
                          <Button
                            onClick={() => setIsAddPatientOpen(true)}
                            className={styles.parentCompactButton}
                            disabled={isPatientCheckedOut}>
                            New patient
                          </Button>
                        </Col>
                      )}
                      {/* Mobile: New patient button in same row when no phone */}
                      {isMobile && !isEditing && !hasPhone && (
                        <Col xs={8}>
                          <Button
                            onClick={() => setIsAddPatientOpen(true)}
                            className={styles.parentCompactButton}
                            disabled={isPatientCheckedOut}>
                            New patient
                          </Button>
                        </Col>
                      )}
                      <Show when={!!errors?.first_name}>
                        <Col xs={24}>
                          <p className='errorMessage'>Please select a patient</p>
                        </Col>
                      </Show>
                    </Row>

                    {/* Mobile layout: Phone on separate row when phone exists */}
                    {isMobile && hasPhone && (
                      <Row gutter={16} align='bottom'>
                        <Col xs={24}>
                          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                            <div style={{ flex: 1 }}>
                              <Input
                                disabled
                                value={values.phone}
                                label='Cell Phone'
                                name='phone_display'
                                readOnly
                              />
                            </div>
                            {!isEditing && (
                              <Button
                                onClick={() => setIsAddPatientOpen(true)}
                                disabled={isPatientCheckedOut}
                                className={styles.parentCompactButton}>
                                New patient
                              </Button>
                            )}
                          </div>
                        </Col>
                      </Row>
                    )}

                    <Row gutter={16} align='top'>
                      <Col xs={12}>
                        <div className={styles.parentSelect}>
                          <FormControl
                            label={NEW_PATIENT_LABELS.APPOINTMENT_WITH}
                            type={INPUT_TYPES.TEXT}
                            name={NEW_PATIENT_FORM_KEYS.CONSULTATION_STAFF}
                            control={FORM_CONTROL_TYPE.SELECT}
                            showSearch={false}
                            options={consultation_staffs
                              ?.filter((user) => user.is_provider || user.is_doctor)
                              .map((consultation_staff: IPatientConsultationStaff) => ({
                                code: consultation_staff.id,
                                name: consultation_staff.name,
                              }))}
                            onChange={(value) => {
                              const updatedErrors = { ...errors }
                              delete updatedErrors.consultation_staff
                              setErrors({ ...updatedErrors })

                              onFieldChange({
                                field: NEW_PATIENT_FORM_KEYS.CONSULTATION_STAFF,
                                fieldValue: value,
                                setFieldValue,
                                values,
                                setErrors,
                                errors,
                              })
                            }}
                          />
                        </div>
                      </Col>
                      <Col xs={12}>
                        <FormControl
                          required
                          label={NEW_PATIENT_LABELS.CATEGORY}
                          type={INPUT_TYPES.TEXT}
                          name={NEW_PATIENT_FORM_KEYS.CATEGORY_TYPE_CODE}
                          control={FORM_CONTROL_TYPE.SELECT}
                          showSearch={false}
                          options={appointmentCategories?.map((category) => ({
                            name: category.name,
                            code: category.code,
                          }))}
                          onChange={(value) => {
                            const updatedErrors = { ...errors }
                            delete updatedErrors.category_type_code
                            setErrors({ ...updatedErrors })

                            onFieldChange({
                              field: NEW_PATIENT_FORM_KEYS.CATEGORY_TYPE_CODE,
                              fieldValue: value,
                              setFieldValue,
                              values,
                              setErrors,
                              errors,
                            })
                          }}
                        />
                      </Col>
                    </Row>

                    <Row gutter={16} align='top'>
                      <Col xs={12}>
                        <FormControl
                          label={NEW_PATIENT_LABELS.STATUS}
                          type={INPUT_TYPES.TEXT}
                          name={NEW_PATIENT_FORM_KEYS.PATIENT_STATUS}
                          control={FORM_CONTROL_TYPE.SELECT}
                          showSearch={false}
                          options={
                            (editedPatientData?.patient_statuses || patientStatuses)?.map(
                              (status) => ({
                                code: status.code,
                                name: status.name,
                              }),
                            ) || []
                          }
                          onChange={(value) => {
                            const updatedErrors = { ...errors }
                            delete updatedErrors.patient_status
                            setErrors({ ...updatedErrors })

                            onFieldChange({
                              field: NEW_PATIENT_FORM_KEYS.PATIENT_STATUS,
                              fieldValue: value,
                              setFieldValue,
                              values,
                              setErrors,
                              errors,
                            })
                          }}
                        />
                      </Col>
                      <Col xs={12}>
                        <FormControl
                          label={NEW_PATIENT_LABELS.TITLE}
                          type={INPUT_TYPES.TEXT}
                          name={NEW_PATIENT_FORM_KEYS.TITLE}
                          control={FORM_CONTROL_TYPE.INPUT}
                          onChange={(e: any) => {
                            const value = e.target.value
                            setFieldValue(NEW_PATIENT_FORM_KEYS.TITLE, value)
                            const updatedErrors = { ...errors }
                            delete updatedErrors.title
                            setErrors({ ...updatedErrors })

                            onFieldChange({
                              field: NEW_PATIENT_FORM_KEYS.TITLE,
                              fieldValue: value,
                              setFieldValue,
                              values,
                              setErrors,
                              errors,
                            })
                          }}
                        />
                      </Col>
                    </Row>

                    <Row gutter={16} align='top'>
                      <Col xs={24} md={12}>
                        <Row gutter={[4, 4]} align='top'>
                          <Col xs={11}>
                            <DatePicker
                              minDate={dayjs()}
                              label='Date'
                              allowClear={false}
                              size='large'
                              value={dayjs(values.date)}
                              onChange={(date) => {
                                setFieldValue('date', date)
                                if (onDateChange) {
                                  onDateChange(date.format('YYYY-MM-DD'))
                                  return
                                }

                                if (!date.isToday()) {
                                  const room = rooms.find((room: any) => room.is_waiting_room)
                                  if (room?.value) {
                                    setFieldValue('room', room.value)
                                  }
                                }
                              }}
                              format='MM-DD-YYYY'
                              style={{ width: '100%' }}
                              status={errors.date && touched.date ? 'error' : undefined}
                              showNow={false}
                            />
                            {errors.date && touched.date && (
                              <p className='errorMessage'>{String(errors.date)}</p>
                            )}
                          </Col>
                          <Col xs={8}>
                            <FormControl
                              label='Time'
                              control={FORM_CONTROL_TYPE.SINGLE_SLOT_TIME_PICKER}
                              name={NEW_PATIENT_FORM_KEYS.TIME}
                              type={FORM_CONTROL_TYPE.SINGLE_SLOT_TIME_PICKER}
                              placeholder='Select time'
                              options={timePickerOptions}
                              onChange={(value) => {
                                setFieldValue('time', value)
                                if (values.duration > calculateMaxDuration(value)) {
                                  setFieldValue('duration', calculateMaxDuration(value))
                                }
                              }}
                              onUpdateField={(value) => {
                                setFieldValue('time', value)
                                if (values.duration > calculateMaxDuration(value)) {
                                  setFieldValue('duration', calculateMaxDuration(value))
                                }
                              }}
                              value={dayjs(values.time).toDate()}
                            />
                          </Col>
                          <Col xs={5}>
                            <div className={styles.parentDurationInput}>
                              <Label label='Duration' id='duration' />
                              <InputNumber
                                placeholder='Duration'
                                id='duration'
                                size='large'
                                value={values.duration}
                                onChange={(value) => {
                                  const newValue = value || 1
                                  const maxDuration = currentMaxDuration
                                  setFieldValue('duration', Math.min(newValue, maxDuration))
                                }}
                                min={1}
                                max={currentMaxDuration}
                                style={{ width: '100%' }}
                                status={errors.duration && touched.duration ? 'error' : undefined}
                              />
                              {errors.duration && touched.duration && (
                                <p className='errorMessage'>{errors.duration}</p>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </Col>
                      <Col xs={24} md={12}>
                        <FormControl
                          required
                          label={NEW_PATIENT_LABELS.ROOM}
                          type={INPUT_TYPES.TEXT}
                          name={NEW_PATIENT_FORM_KEYS.ROOM}
                          control={FORM_CONTROL_TYPE.SELECT}
                          showSearch={false}
                          options={rooms.map((room) => ({
                            name: room.short_name ?? formatRoomName(room.label),
                            code: room.value,
                          }))}
                          onChange={(value) => {
                            const updatedErrors = { ...errors }
                            delete updatedErrors.room
                            setErrors({ ...updatedErrors })

                            onFieldChange({
                              field: NEW_PATIENT_FORM_KEYS.ROOM,
                              fieldValue: value,
                              setFieldValue,
                              values,
                              setErrors,
                              errors,
                            })
                          }}
                        />
                        {roomErrorMessage && (
                          <p className={styles.parentRoomError}>{roomErrorMessage}</p>
                        )}
                      </Col>
                    </Row>

                    {((!!editedPatientData?.sms_notification &&
                      editedPatientData?.sms_notification !== SMS_STATUSES.UNCONFIRMED) ||
                      (!!editedPatientData?.error_sms_message &&
                        editedPatientData?.error_sms_message !== '')) && (
                      <Row gutter={16} align='top'>
                        <Col xs={24}>
                          <Collapse
                            defaultActiveKey={[]}
                            items={[
                              {
                                key: 'sms-status',
                                label: (
                                  <span className={styles.parentPatientStatusTitle}>
                                    SMS Status
                                  </span>
                                ),
                                children: (
                                  <div>
                                    {editedPatientData?.notification_title && (
                                      <p className='p-0 m-0'>
                                        {editedPatientData.notification_title}
                                      </p>
                                    )}
                                    {editedPatientData?.error_sms_message && (
                                      <p className='p-0 m-0'>
                                        {editedPatientData.error_sms_message}
                                      </p>
                                    )}
                                  </div>
                                ),
                              },
                            ]}
                          />
                        </Col>
                      </Row>
                    )}
                    {siteId !== BEVERLY_HILLS_SECOND_PRODUCTION && (
                      <Row gutter={16} align='top'>
                        <Col xs={24} md={12}>
                          <div className={styles.parentEHRTemperatureCheck}>
                            <FormControl
                              label={NEW_PATIENT_LABELS.TEMPERATURE_BYPASS}
                              type={INPUT_TYPES.CHECKBOX}
                              name={NEW_PATIENT_FORM_KEYS.TEMPERATURE_BYPASS}
                              control={FORM_CONTROL_TYPE.CHECKBOX}
                            />
                          </div>
                        </Col>
                      </Row>
                    )}
                    <Row align='top' className={styles.parentCommentsSection}>
                      <Col xs={24}>
                        <FormControl
                          label={NEW_PATIENT_LABELS.COMMENTS}
                          type={INPUT_TYPES.TEXT}
                          name={NEW_PATIENT_FORM_KEYS.COMMENTS}
                          control={FORM_CONTROL_TYPE.TEXTAREA}
                          rows={2}
                          placeholder='Enter any additional comments...'
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={24}>
                        <Collapse
                          defaultActiveKey={[]}
                          items={[
                            {
                              key: 'sms-options',
                              label: 'SMS Options (Appointment settings override Account settings)',
                              children: (
                                <SmsOptions
                                  patientBufferTime={patientBufferTime}
                                  appointmentPunctualityType={values.punctuality_type_code}
                                  appointmentPunctualityMinutes={values.punctuality_minutes}
                                  patientPunctuality={values.patient_punctuality}
                                  punctuality={punctuality}
                                  onChangePunctualityType={(value) => {
                                    setFieldValue(
                                      NEW_PATIENT_FORM_KEYS.PUNCTUALITY_TYPE_CODE,
                                      value,
                                    )
                                  }}
                                  onChangePunctualityValue={(value) => {
                                    setFieldValue(NEW_PATIENT_FORM_KEYS.PUNCTUALITY_MINUTES, value)
                                  }}
                                  error={errors.punctuality_minutes}
                                  touched={touched.punctuality_minutes}
                                />
                              ),
                            },
                          ]}
                        />
                      </Col>
                    </Row>

                    {!!editedPatientData?.created_by && (
                      <div>
                        <p className='m-0'>
                          Originally created by {editedPatientData.created_by.user_name} on{' '}
                          {formatAuditDateTime(editedPatientData.created_by.created_at)}
                        </p>
                      </div>
                    )}

                    {editedPatientData?.updated_at && (
                      <div>
                        <p className='m-0'>
                          Last updated by {editedPatientData.updated_by} on{' '}
                          {formatAuditDateTime(editedPatientData.updated_at)}
                        </p>
                      </div>
                    )}

                    {!!editedPatientData &&
                      (!!patientAccessCode ||
                        REMOTE_APPOINTMENT_CATEGORY_CODES.includes(
                          Number(values.category_type_code),
                        )) && (
                        <>
                          <div className={styles.parentBottomActions}>
                            {!!patientAccessCode && (
                              <p className={styles.parentPatientCode}>
                                {INFO_CONSTANTS.PATIENT_CODE}:{' '}
                                <span>{editedPatientData?.access_code}</span>
                              </p>
                            )}

                            {editedPatientData?.status.code !== PATIENT_STATUSES.CHECKED_OUT && (
                              <>
                                <Button
                                  className={styles.parentNotifyButton}
                                  modifier={BUTTON_MODIFIER.SECONDARY}
                                  proportion={BUTTON_PROPORTION.SMALL}
                                  onClick={() => setNotifyOpen(true)}
                                  disabled={isLoading || Date.now() < disabledUntil}>
                                  {BUTTON_CONSTANTS.NOTIFY_PATIENT}
                                </Button>
                                {!!telehealthPatientLink && (
                                  <button
                                    type='button'
                                    className={styles.parentCopyLinkButton}
                                    aria-label='Copy telehealth link'
                                    title='Copy telehealth link to send manually'
                                    onClick={handleCopyTelehealthLink}>
                                    <Icon icon='ph:copy' fontSize={16} />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                          {editedPatientData?.last_notification && (
                            <p className={styles.parentLastSMS}>
                              {INFO_CONSTANTS.LAST_SMS_SENT}{' '}
                              <span>
                                {format(
                                  parse(
                                    editedPatientData.last_notification.replace(
                                      'Last Text Message sent on ',
                                      '',
                                    ),
                                    `MM/dd/yyyy 'at' ${TIME_FORMAT.TWELVE_HOUR_FORMAT_WITH_SECONDS}`,
                                    new Date(),
                                  ),
                                  `MM/dd/yyyy 'at' ${TIME_FORMAT.TWELVE_HOUR_FORMAT}`,
                                )}
                              </span>
                            </p>
                          )}
                        </>
                      )}

                    <div className={styles.parentFormActions}>
                      <div className={styles.parentFormSubmitLeft}>
                        {!isPatientCheckedOut && (
                          <Button
                            className={styles.parentSubmitButton}
                            modifier={BUTTON_MODIFIER.SECONDARY}
                            onClick={() => setIsFindAvailableOpen(true)}
                            disabled={isLoading}>
                            Find Available
                          </Button>
                        )}
                        {editedPatientData?.id !== undefined && (
                          <JitsiTelehealthCallButton
                            schedule={editedPatientData}
                            disabled={isLoading}
                            onCallStarted={(statusTypeCode) => {
                              formRef.current?.setFieldValue(
                                NEW_PATIENT_FORM_KEYS.PATIENT_STATUS,
                                statusTypeCode,
                              )
                            }}
                          />
                        )}
                      </div>

                      <div className={styles.parentFormSubmitRight}>
                        <Button
                          className={styles.parentSubmitButton}
                          htmlType='submit'
                          data-cy='submit-btn'
                          loading={isLoading}
                          disabled={Boolean(roomErrorMessage) || !!errors?.appointment}
                          severity={BUTTON_SEVERITY.SUCCESS_FILLED}>
                          {BUTTON_CONSTANTS.SAVE}
                        </Button>

                        {displayDeletePatientTrash && (
                          <Button
                            className={styles.parentSubmitButton}
                            severity={BUTTON_SEVERITY.DANGER}
                            modifier={BUTTON_MODIFIER.PRIMARY}
                            onClick={handleShowDeleteConfirmation}
                            disabled={isLoading || isLoadingDelete}>
                            {BUTTON_CONSTANTS.DELETE}
                          </Button>
                        )}

                        <Button
                          className={styles.parentSubmitButton}
                          data-cy='cancel-btn'
                          loading={isLoading}
                          onClick={() => {
                            clearModalData()
                            resetForm()
                          }}>
                          {BUTTON_CONSTANTS.CANCEL}
                        </Button>
                      </div>
                    </div>
                  </Form>
                </Modal>
              )
            }}
          </Formik>
          <Warning
            open={Boolean(awaitConfirmationSchedule)}
            title={INFO_CONSTANTS.ARE_YOU_SURE}
            warning={INFO_CONSTANTS.MULTIPLE_PATIENTS_TITLE}
            okButtonText={INFO_CONSTANTS.YES}
            handleClose={() => setAwaitConfirmationSchedule(null)}
            handleConfirm={handleConfirmUpdateAppointment}
            hasCancel
          />

          <ModalAction
            destroyOnHidden
            isOpen={isNotifyOpen}
            title={MODAL_LABELS.NOTIFY_PATIENT}
            confirmText={BUTTON_CONSTANTS.SEND}
            cancelText={BUTTON_CONSTANTS.CANCEL}
            onConfirm={() => {
              setNotifyOpen(false)
              handleNotify()
            }}
            onClose={() => setNotifyOpen(false)}
            description={PATIENTS_LABELS.CONFIRM_NOTIFY}
            confirmButtonSeverity={BUTTON_SEVERITY.SUCCESS_FILLED}
          />

          {isPatientSearchOpen && (
            <PatientSearchModal
              isOpen={isPatientSearchOpen}
              onClose={() => setIsPatientSearchOpen(false)}
              onSelectPatient={handlePatientSelect}
            />
          )}
          <FindAvailableModal
            isOpen={isFindAvailableOpen}
            onClose={() => setIsFindAvailableOpen(false)}
            onSelectTime={handleFindAvailableTimeSelect}
            defaultDate={dayjs(formRef?.current?.values?.date).format('YYYY-MM-DD')}
            duration={formRef?.current?.values?.duration || 5}
            roomType={
              rooms.find((room) => String(room.value) === String(formRef?.current?.values?.room))
                ?.room_type_id ?? 0
            }
            isDateDisabled={isDateDisabled}
          />
          <DeleteConfirmationDialog
            isOpen={isDeleteConfirmOpen}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            isLoading={isLoadingDelete}
          />
          <EncounterSelectModal
            isOpen={isEncounterModalOpen}
            onClose={() => setIsEncounterModalOpen(false)}
            patientId={editedPatientData?.patient?.id}
            selectedEncounterId={mappedEncounterId ?? editedPatientData?.emr_encounter_id ?? null}
            errorMessage={encounterError}
            isResolving={isOverwritingEncounter}
            confirmButtonText={BUTTON_CONSTANTS.SAVE}
            onConfirm={handleEncounterConfirm}
          />
        </div>
      </>
    )
  },
)
